import { useState } from "react";
import { Loader2 } from "lucide-react";
import { buildPayload, useSubmit, validateContact, validateLocation } from "../../lib/forms";
import { ChoiceCards, ConsentBlock, ContactFields, Field, FormError, Group, Honeypot, LocationFields, MultiChips, NativeSelect, SuccessPanel, TextArea, TextField } from "./FormBits";

const CATEGORIES = [
  { value: "care_referrals", label: "Care referrals", desc: "Case managers, social workers, discharge planners" },
  { value: "housing_organization", label: "Housing organization", desc: "Housing providers and programs" },
  { value: "community_agency", label: "Community agency", desc: "Community and social service organizations" },
  { value: "property_owner", label: "Property owner", desc: "Rent your home to us so we can operate a housing program for the people we serve" },
  { value: "landlord", label: "Landlord", desc: "Lease your rental property to us for one of our housing programs" },
  { value: "property_manager", label: "Property manager", desc: "Lease a property you manage to us for one of our housing programs" },
  { value: "other", label: "Other partnership", desc: "Another kind of collaboration" },
];
const INTERESTS = ["Care referrals", "Housing opportunities", "Property arrangements", "Community partnerships", "Caregiver recruitment", "Information sharing"];
const PROPERTY = ["property_owner", "landlord", "property_manager"];
const REGIONS = [
  { value: "gta", label: "Greater Toronto Area", desc: "Ontario, Canada" },
  { value: "other", label: "Another location", desc: "Tell us where" },
];

const INITIAL = {
  organization: "", role: "", partner_category: "", partner_category_other: "", collaboration_interests: [], property_type: "", property_type_other: "", property_units: "",
  property_location: "", description: "", region: "", city: "", city_custom: "", name: "", email: "", phone: "",
  preferred_contact: "", privacy_consent: false, marketing_consent: false, website: "",
};

const validate = (f) => {
  if (!f.partner_category) return "Please select a partnership category.";
  if (f.partner_category === "other" && !f.partner_category_other.trim()) return "Please describe the type of partnership.";
  if (PROPERTY.includes(f.partner_category) && f.property_type === "Other" && !f.property_type_other.trim()) return "Please describe the property type.";
  const c = validateContact({ ...f, privacy_consent: true });
  if (c) return c;
  if (!f.organization.trim()) return "Please enter your organization (or 'Independent' if not applicable).";
  if (!f.role.trim()) return "Please enter your professional role.";
  const l = validateLocation(f);
  if (l) return l;
  if (f.description.trim().length < 10) return "Please describe the proposed collaboration (at least 10 characters).";
  return f.privacy_consent ? null : "Please confirm that you have read the Privacy Notice.";
};

export const PartnerForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [localErr, setLocalErr] = useState("");
  const { submit, submitting, error, reference, reset } = useSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isProperty = PROPERTY.includes(form.partner_category);

  if (reference) {
    return (
      <SuccessPanel reference={reference} title="Thank you for reaching out." testId="partner-success" onReset={() => { setForm(INITIAL); reset(); }}>
        <p>Our team will review your inquiry and follow up to continue the conversation. Submitting this form does not create a referral, property or partnership agreement.</p>
      </SuccessPanel>
    );
  }

  const onSubmit = (e) => {
    e.preventDefault();
    const err = validate(form);
    setLocalErr(err || "");
    if (!err) {
      const payload = isProperty ? { ...form } : { ...form, property_type: "", property_units: "", property_location: "" };
      if (payload.property_type !== "Other") payload.property_type_other = "";
      if (form.partner_category !== "other") payload.partner_category_other = "";
      submit("/public/partner-inquiry", buildPayload(payload));
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate className="card-warm p-6 sm:p-10 space-y-8" data-testid="partner-form">
      <Group legend="Partnership category" required>
        <ChoiceCards options={CATEGORIES} value={form.partner_category} onChange={(v) => set("partner_category", v)} cols="sm:grid-cols-2 lg:grid-cols-3" testId="partner-category" />
      </Group>
      {form.partner_category === "other" && (
        <TextField id="partner-category-other" label="Please describe the type of partnership" value={form.partner_category_other} onChange={(v) => set("partner_category_other", v)} required maxLength={120} testId="partner-input-category-other" />
      )}
      <ContactFields form={form} set={set} prefix="partner" />
      <div className="grid sm:grid-cols-2 gap-5">
        <TextField id="partner-org" label="Organization" value={form.organization} onChange={(v) => set("organization", v)} required hint="Enter 'Independent' if you are an individual owner." testId="partner-input-organization" />
        <TextField id="partner-role" label="Professional role" value={form.role} onChange={(v) => set("role", v)} required placeholder="e.g. Case Manager, Owner" hint="Your title or role." testId="partner-input-role" />
      </div>
      <LocationFields form={form} set={set} prefix="partner" regions={REGIONS} legend="Your location" />
      {isProperty && (
        <div className="grid sm:grid-cols-3 gap-5 rounded-2xl bg-[#F3EFEA]/60 p-5 animate-step" data-testid="partner-property-fields">
          <Field label="Property type" htmlFor="partner-ptype">
            <NativeSelect id="partner-ptype" value={form.property_type} onChange={(v) => set("property_type", v)} testId="partner-input-property-type"
              options={["Detached house", "Semi-detached or townhouse", "Multi-unit building", "Condo or apartment", "Other"]} />
          </Field>
          {form.property_type === "Other" && (
            <TextField id="partner-ptype-other" label="Describe the property type" value={form.property_type_other} onChange={(v) => set("property_type_other", v)} required maxLength={120} testId="partner-input-property-type-other" />
          )}
          <Field label="Number of units or rooms" htmlFor="partner-units">
            <NativeSelect id="partner-units" value={form.property_units} onChange={(v) => set("property_units", v)} testId="partner-input-property-units"
              options={["1", "2–4", "5–10", "More than 10"]} />
          </Field>
          <TextField id="partner-ploc" label="General property area" value={form.property_location} onChange={(v) => set("property_location", v)} placeholder="City or neighbourhood" testId="partner-input-property-location" />
        </div>
      )}
      <Group legend="Areas of interest (optional)">
        <MultiChips options={INTERESTS} value={form.collaboration_interests} onChange={(v) => set("collaboration_interests", v)} testId="partner-interest" />
      </Group>
      <TextArea id="partner-description" label="Short description of the proposed collaboration" value={form.description} onChange={(v) => set("description", v)} required max={2000}
        hint="Please do not include personal details about clients or residents." testId="partner-input-description" />
      <ConsentBlock form={form} set={set} prefix="partner" purpose="partnership inquiry" />
      <Honeypot value={form.website} onChange={(v) => set("website", v)} />
      <FormError message={localErr || error} testId="partner-error" />
      <button type="submit" disabled={submitting} className="btn-pill btn-primary disabled:opacity-60" data-testid="partner-submit-button">
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : "Submit partnership inquiry"}
      </button>
    </form>
  );
};
